import { Component, ElementRef, Input, OnInit, ViewChild, Inject, LOCALE_ID, ErrorHandler } from '@angular/core';
import * as moment from 'moment';
import { AvailabilityModel, AvailabilityProvider } from 'src/app/providers/availability.provider';
import { PricingSchemas } from 'src/app/models/enums';
import { BasketItemOption, OptionQuantity } from 'src/app/models/product-order.model';
import { DialogService } from 'src/app/services/dialog.service';
import Utils from '../../shared/utils';
import { ContinueDialogResult } from '../continue-dialog/continue-dialog.component';
import { Moment } from 'moment';
import { ProductOptionModel } from 'src/app/models/product-option.model';
import { ProductModel } from 'src/app/models/product.model';
import { LanguageModel } from 'src/app/models/language.model';
import { NavigationService } from 'src/app/services/navigation.service';
import { ProductTypeId } from 'src/app/models/enums/product-type.enum';
import { API_DAY_FORMAT } from 'src/app/shared/date-utils';
import { DatePickerComponent } from '../date-picker/date-picker.component';
import { CartService } from 'src/app/services/cart/cart.service';
import { SoloModeProvider } from 'src/app/providers/solo-mode.provider';
import { PricingScheme } from 'src/app/models/pricing-schema.model';

@Component({
  selector: 'app-add-option',
  templateUrl: './add-option.component.html',
  styleUrls: ['./add-option.component.scss'],
})
export class AddOptionComponent implements OnInit {
  @Input() public expanded;
  @Input() public option: ProductOptionModel;
  @Input() public languages: LanguageModel[];

  @ViewChild('calendar') calendar: DatePickerComponent;

  public product: ProductModel;
  public availability: AvailabilityModel[];
  public availabilitiesForPeriod: UiAvailabilityModel[] = [];
  public maxAdults: number;

  public selectedDate: Moment;
  public selectedAvailability: UiAvailabilityModel;
  public selectedLanguageCode: LanguageCode;
  public selectedParticipants = { adults: 0, children: 0 };

  public availablePricingSchemes: AvailabilityPricingSchemasGroupItem[];
  public availableLanguages: LanguageCode[] = [];
  public viewInterval = { begin: moment(), end: moment() };

  public availableDates: string[] = [];
  public hasError = false;
  public errorMessage: string;
  public loadingAvailability: boolean;
  public isSoldOutEvent = false;
  isSoloMode: boolean;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    public navigationService: NavigationService,
    private cartService: CartService,
    private dialogService: DialogService,
    private availabilityProvider: AvailabilityProvider,
    private errorHandler: ErrorHandler,
    private soloModeProvider: SoloModeProvider,
    @Inject(LOCALE_ID) private locale: string
  ) {}

  ngOnInit() {
    this.isSoloMode = this.soloModeProvider.isSoloMode;

    this.product = this.option.product;
    this.getCurrentView(moment());

    if (this.selectedAvailability) {
      this.onTimeSelected(this.selectedAvailability);
    }
  }

  get isEvent(): boolean {
    return this.product.productTypeId == ProductTypeId.event;
  }

  private async getCurrentView(dateMoment: Moment) {
    const begin = dateMoment.clone().startOf('month');
    const end = dateMoment.clone().endOf('month');

    this.viewInterval = { begin, end };
    await this.loadAvailability(this.viewInterval.begin, this.viewInterval.end);
  }

  private async loadAvailability(from: Moment, to: Moment): Promise<void> {
    this.loadingAvailability = true;

    try {
      const { availabilities } = await this.availabilityProvider.loadAvailability(
        from.toDate(),
        to.toDate(),
        this.option.id,
        this.product.currency
      );

      this.availability = availabilities;
      this.availableDates = availabilities.map((a) => a.day);

      if (this.isEvent) {
        if (availabilities.length === 0) {
          this.isSoldOutEvent = true;
        } else {
          this.selectEventAvailability(availabilities[0]);
        }
      } else {
        this.calendar.update();
      }
    } catch (error) {
      this.errorHandler.handleError(error);
      this.hasError = true;

      if (typeof error.error === 'string') {
        this.errorMessage = error.error;
      }
    }

    this.loadingAvailability = false;
  }

  private selectEventAvailability(availability: AvailabilityModel) {
    this.onDateSelect(moment(availability.startDate));
    this.onTimeSelected(this.availabilitiesForPeriod[0]);
  }

  private resetAvailabilities() {
    this.selectedDate = null;
    this.availablePricingSchemes = [];
    this.availabilitiesForPeriod = [];
    this.selectedAvailability = null;
    this.setAvailableLanguages(null);
  }

  public onDateSelect(date: Moment) {
    this.resetAvailabilities();
    this.selectedDate = date;
    this.availabilitiesForPeriod = this.availability
      .filter((av) => Utils.getFormatedDate(av.day) === Utils.getFormatedDate(date))
      .map((av) => {
        return {
          ...av,
          pricingSchemas: av.pricingSchemas as PricingScheme[],
          display: this.availabilityName(av),
          selected: false,
        };
      });
    if (this.availabilitiesForPeriod.length == 1) {
      this.onTimeSelected(this.availabilitiesForPeriod[0]);
    }
  }

  public onTimeSelected(availability: UiAvailabilityModel) {
    this.availabilitiesForPeriod.forEach((av) => (av.selected = false));
    availability.selected = true;
    this.setupAvailableSchemas(availability);
    this.selectedAvailability = availability;
    this.setAvailableLanguages(availability);
  }

  public increaseCount(schemaGroupItem: AvailabilityPricingSchemasGroupItem): void {
    const remainingCapacity = this.remainingCapacity();

    if (remainingCapacity <= this.currentInProgressCapacity()) {
      return;
    }

    const newCount = schemaGroupItem.count + 1;
    const schema = schemaGroupItem.schemas.find(
      (schema) => schema.minimumCapacity <= newCount && schema.maximumCapacity >= newCount
    );

    if (schema) {
      schemaGroupItem.count = newCount;
      schemaGroupItem.selectedSchema = schema;
    }
  }

  public decreaseCount(schemaGroupItem: AvailabilityPricingSchemasGroupItem): void {
    const newCount = schemaGroupItem.count - 1;

    const schema = schemaGroupItem.schemas.find(
      (schema) => schema.minimumCapacity <= newCount && schema.maximumCapacity >= newCount
    );

    if (schema) {
      schemaGroupItem.count = newCount;
      schemaGroupItem.selectedSchema = schema;
    }
  }

  public addToShoppingCart() {
    const bookedOption: BasketItemOption = {
      name: this.option.name,
      optionId: this.option.id,
      languageCode: this.selectedLanguageCode,
      productTypeId: this.product.productTypeId,
    };

    if (this.product.useRealTimeAvailability) {
      bookedOption.from = moment(this.selectedAvailability.startDate).format(API_DAY_FORMAT);
      bookedOption.startTime = this.selectedAvailability.startTime;
      bookedOption.to = moment(this.selectedAvailability.endDate).format(API_DAY_FORMAT);
      bookedOption.endTime = this.selectedAvailability.endTime;
      bookedOption.currency = this.product.currency;

      bookedOption.optionQuantities = this.availablePricingSchemes
        .filter((s) => s.count !== 0)
        .map((scheme) => {
          return {
            quantity: scheme.count,
            pricingSchemaId: scheme.pricingSchemaId,
            price: scheme.selectedSchema.rate * scheme.count,
            name: scheme.selectedSchema.name,
          } as OptionQuantity;
        });
      bookedOption.totalPrice = +bookedOption.optionQuantities
        .reduce((totalPrice, quntity) => totalPrice + quntity.price, 0)
        .toFixed(2);
    }

    this.cartService.addOption(bookedOption);

    if (this.isSoloMode) {
      this.navigationService.goToCart();
      return;
    }

    this.dialogService
      .openContinueDialog(this.elementRef.nativeElement)
      .afterClosed()
      .subscribe((result?: ContinueDialogResult) => {
        if (result && result.navigateToCart) {
          this.navigationService.goToCart();
        }
      });
  }

  setupAvailableSchemas(availability: UiAvailabilityModel) {
    this.availablePricingSchemes = availability.pricingSchemas.reduce<AvailabilityPricingSchemasGroupItem[]>(
      (schemaGroups, schema) => {
        let groupItem = schemaGroups.find((item) => item.pricingSchemaId === schema.pricingSchemaId);

        if (!groupItem) {
          groupItem = {
            pricingSchemaId: schema.pricingSchemaId,
            schemas: [schema],
            count: schema.minimumCapacity,
            selectedSchema: schema,
          };
          schemaGroups.push(groupItem);
        } else {
          groupItem.schemas.push(schema);

          if (schema.minimumCapacity < groupItem.count) {
            groupItem.count = schema.minimumCapacity;
            groupItem.selectedSchema = schema;
          }
        }

        return schemaGroups;
      },
      []
    );
  }

  public onMonthChange(monthDate: Moment) {
    this.resetAvailabilities();
    this.getCurrentView(monthDate);
  }

  public getLanguageByCode(code: string) {
    const found = this.languages.find((lang) => lang.code === code);

    return found ? found.name : '';
  }

  public isAddButtonEnabled() {
    const isAnyDateSelected = !!this.selectedDate;
    const hasAnySchedules = this.availabilitiesForPeriod.length > 0;
    const isAnyScheduleSelected = !!this.selectedAvailability;
    const hasCapacity = () => {
      const remainingCapacity = this.remainingCapacity();
      const minimumSchemasCapacity = this.availablePricingSchemes.reduce(
        (total, groupItem) => (total += Math.min(...groupItem.schemas.map((x) => x.minimumCapacity))),
        0
      );
      return remainingCapacity >= minimumSchemasCapacity && remainingCapacity >= this.currentInProgressCapacity();
    };

    return isAnyDateSelected && ((hasAnySchedules && isAnyScheduleSelected) || !hasAnySchedules) && hasCapacity();
  }

  public retry() {
    this.getCurrentView(moment());
    this.hasError = false;
  }

  public remainingCapacity(): number {
    return this.cartService.remainingCapacity(this.selectedAvailability);
  }

  getProductTypeExperienceName() {
    switch (this.product.productTypeId) {
      case ProductTypeId.hotel:
        return 'Room Type';
      case ProductTypeId.tour:
        return 'Tour';
      default:
        return 'Experience';
    }
  }

  // This needs to be bound with the fat arrow (=>) because it will be
  // passed into the app-calendar’s dateFilter property.
  isDateAvailable = (date: Moment): boolean => this.availableDates.includes(Utils.getFormatedDate(date));

  private availabilityName(av: AvailabilityModel): string {
    return av.startTime ? moment(Utils.getFormatedDate(av.startDate) + 'T' + av.startTime).format('H:mm') : 'All day';
  }

  public formattedEventDate(selectedAvailability: UiAvailabilityModel | undefined): string {
    if (!selectedAvailability) {
      return '-';
    }

    return moment(selectedAvailability.day).locale(this.locale).format('dddd, Do MMMM');
  }

  private setAvailableLanguages(selectedAvailability: UiAvailabilityModel | null): void {
    let languageCodes = [];

    if (!selectedAvailability) {
      languageCodes = [];
    } else if (selectedAvailability.languages.length > 0) {
      languageCodes = selectedAvailability.languages;
    } else if (this.option.languages.length > 0) {
      languageCodes = this.option.languages;
    } else if (this.option.defaultLanguage) {
      languageCodes = [this.option.defaultLanguage];
    }

    this.availableLanguages = languageCodes;
    this.selectedLanguageCode = this.selectedLanguageCode || languageCodes[0];
  }

  public get shouldDisplayUi(): boolean {
    if (this.isEvent && this.isSoldOutEvent) {
      return false;
    }

    if (this.isEvent && this.loadingAvailability) {
      return false;
    }

    return true;
  }

  public currentInProgressCapacity(): number {
    return this.availablePricingSchemes.reduce((sum, groupItem) => sum + groupItem.count, 0);
  }
}

interface UiAvailabilityModel extends AvailabilityModel {
  pricingSchemas: PricingScheme[];
  selected: boolean;
  display: string;
}

interface AvailabilityPricingSchemasGroupItem {
  pricingSchemaId: PricingSchemas;
  schemas: PricingScheme[];
  count: number;
  selectedSchema: PricingScheme;
}

type LanguageCode = string;
